import { Alert, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";

import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import BackButton from "components/UI/BackButton";
import { getPermission, getUserData } from "helpers";

import { getPermissions, updatePermission } from "framework/permissions";
import PermissionTable from "./partials/PermissionList";
import { IPermission } from "models/permissions";
import EmptyContent from "components/EmptyContent";
import BlockIcon from "@mui/icons-material/Block";

interface Props {
  feature: Feature;
}

export const RolesPermission: React.FC<Props> = ({ feature }) => {
  const urlParams = useParams();
  let id: string = urlParams[`${feature.singleName}Id`] as string;
  const [permissions, setPermissions] = useState<IPermission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loading3, setLoading3] = useState<boolean>(true);
  const [permission, setPermission] = useState<IPermission | null>(null);
  const [isError, setIsError] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [loading2, setLoading2] = useState<boolean>(false);
  const user = getUserData();
  const { colors } = user;
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const updatePermissionMutation = useMutation({
    mutationFn: (data: { createInput: any; id: string }) => {
      return updatePermission(data.createInput, id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let response: any = await queryClient.fetchQuery(
        "get permission Lists",
        () => getPermissions("/" + id)
      );
      const formatedPermissions = response.map((item: IPermission) => {
        return { ...item, roleId: id };
      });
      setPermissions(formatedPermissions);
      setLoading(false);
    } catch (error: any) {
      setIsError(true);
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchPermission = useCallback(async () => {
    setLoading3(true);
    try {
      const res = await getPermission(27);
      setPermission(res);
      setLoading3(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading3(false);
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
    fetchPermission();
    // eslint-disable-next-line
  }, []);

  if (loading || loading3)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  if (!permission?.hasReadPermission)
    return (
      <EmptyContent
        icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
        header={t("T_no_permissions")}
        message={t("T_no_permissions_message")}
      />
    );
  if (isError)
    return (
      <Alert severity="error">
        Something went wrong, please try again later.
      </Alert>
    );

  const handleSubmit = async (values: any) => {
    try {
      setLoading2(true);

      await updatePermissionMutation.mutateAsync({
        createInput: values,
        id,
      });
      setAlert({
        open: true,
        message: "Permission is updated successfully",
        type: "success",
      });
      fetchData();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  return (
    <>
      <div>
        <BackButton path="/roles" text={t("T_back_to_roles")} />
      </div>

      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
            style={{ paddingBlock: "30px" }}
          >
            {t("T_permissions_list")}
          </Typography>
        </Grid>

        <PermissionTable permissions={permissions} onSubmit={handleSubmit} />
      </Stack>
    </>
  );
};
